'use client';

import PropTypes from 'prop-types';
import { Box, Container, Stack, Typography } from '@mui/material';
import WebsiteButton from '@/components/website/WebsiteButton';
import MarketingReveal from '@/components/website/Marketing/MarketingReveal';
import {
  resolvedImageAlt,
  resolvedImageUrl,
} from '@/components/website/MarketingPage/marketing-page-utils';

const TRADE_CTA_ANCHOR_ID = 'Trade-CTA';

export default function TradeCTA({
  id,
  title = '',
  body = '',
  imageAssetId = null,
  imageAlt = '',
  cta1Label = '',
  cta1Url = '',
  cta2Label = '',
  cta2Url = '',
}) {
  const imageUrl = resolvedImageUrl(imageAssetId);
  const resolvedAlt = imageAlt || resolvedImageAlt(imageAssetId, '');
  const buttons = [
    cta1Label && cta1Url ? { label: cta1Label, href: cta1Url } : null,
    cta2Label && cta2Url ? { label: cta2Label, href: cta2Url } : null,
  ].filter(Boolean);

  return (
    <MarketingReveal
      id={id && id !== TRADE_CTA_ANCHOR_ID ? id : undefined}
      from="up"
      animate={false}
    >
      <Box
        id={TRADE_CTA_ANCHOR_ID}
        component="section"
        sx={{
          position: 'relative',
          py: { xs: 8, md: 12 },
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={resolvedAlt}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.3,
              pointerEvents: 'none',
            }}
          />
        ) : null}
        <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
          {title ? (
            <MarketingReveal observe={false} delay={0} from="up">
              <Typography variant="h4" sx={{ fontWeight: 500, mb: 1 }}>
                {title}
              </Typography>
            </MarketingReveal>
          ) : null}
          {body ? (
            <MarketingReveal observe={false} delay={0.12} from="up">
              <Typography sx={{ lineHeight: 1.7, mb: 3 }}>{body}</Typography>
            </MarketingReveal>
          ) : null}
          {buttons.length > 0 ? (
            <MarketingReveal observe={false} delay={0.24} from="up">
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems="center"
                justifyContent="center"
              >
                {buttons.map(button => (
                  <WebsiteButton key={`${button.href}-${button.label}`} href={button.href}>
                    {button.label}
                  </WebsiteButton>
                ))}
              </Stack>
            </MarketingReveal>
          ) : null}
        </Container>
      </Box>
    </MarketingReveal>
  );
}

TradeCTA.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.string,
  imageAssetId: PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string]),
  imageAlt: PropTypes.string,
  cta1Label: PropTypes.string,
  cta1Url: PropTypes.string,
  cta2Label: PropTypes.string,
  cta2Url: PropTypes.string,
};
